import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNotificationService } from '../service';
import { NotificationKeys, NotificationListParams } from '../models';
import { useToast } from '@/shared/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { BackendErrorResponse } from '@/shared';

export const NOTIFICATION_QUERY_KEYS = {
  all: ['notifications'] as const,
  lists: () => [...NOTIFICATION_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: NotificationListParams) =>
    [...NOTIFICATION_QUERY_KEYS.lists(), filters] as const,
  details: () => [...NOTIFICATION_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...NOTIFICATION_QUERY_KEYS.details(), id] as const,
} as const;

/**
 * Hook returns all notification mutations
 *
 */ export function useNotificationMutations() {
  const service = useNotificationService();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation('notification');

  const invalidateLists = () =>
    queryClient.invalidateQueries({
      queryKey: NOTIFICATION_QUERY_KEYS.lists(),
    });

  const notification = (
    message: string,
    type: 'info' | 'success' | 'error' | 'warning' | 'message'
  ) => {
    notify(message, { position: 'bottom-right', duration: 2000 }, type);
  };

  return {
    // List Notification
    useListNotifications: (params?: {
      page?: number;
      limit?: number;
      searchTerm?: string;
      sortBy?: string;
      sortOrder?: string;
      customerID?: string;
      tenantId?: string;
    }) =>
      useQuery({
        queryKey: NOTIFICATION_QUERY_KEYS.list(params),
        queryFn: () => service.listNotifications(params),
        staleTime: params?.customerID ? 0 : 5 * 60 * 1000,
      }),

    //Create Notification
    createNotification: useMutation({
      mutationFn: (formData: NotificationKeys) =>
        service.createNotification(formData),
      onSuccess: () => {
        notification(
          t('notification.actionSuccess', {
            action: t('notification.creation'),
          }),
          'success'
        );
        invalidateLists();
      },
      onError: (error: BackendErrorResponse) => {
        notification(error.response?.data?.message ?? error.message, 'error');
      },
    }),

    // Get Notification by ID
    useGetNotificationById: (id: string) =>
      useQuery({
        queryKey: NOTIFICATION_QUERY_KEYS.detail(id),
        queryFn: () => service.getNotificationById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
      }),
  };
}
